import type { RebalanceAdvisorData } from '../api/rebalance-advisor';
import type { RebalancingResponse } from '../api/rebalancing-targets';
import type { DriftHandoffContext, DriftHandoffSource } from './driftHandoff';

export type RebalancingSurface = 'rebalancing-targets' | 'rebalancing';
export type RebalancingDominantState =
  | 'save-targets'
  | 'review-largest-drifts'
  | 'review-suggested-trades'
  | 'within-tolerance';

export type RebalancingCompletionProofSource = DriftHandoffSource | 'local-save';

export interface PersistedRebalancingCompletionProof {
  source?: RebalancingCompletionProofSource;
  proofLabel: string;
  proofOccurredAt?: string;
  activeGoalName?: string;
  goalTolerancePct?: number;
  dominantState: RebalancingDominantState;
  beforeAssetsWithDrift?: number;
  afterAssetsWithDrift: number;
  topDriftSymbol?: string;
  topDriftPct?: number;
  targetCoverageCount: number;
  trackedAssetCount: number;
  topPrioritySymbol?: string;
  topPriorityDriftPct?: number;
  topSuggestedTradeSymbol?: string;
  topSuggestedTradeAction?: 'BUY' | 'SELL';
  topSuggestedTradeAmountUsd?: number;
  targetUpdatedAt?: string | null;
}

const STORAGE_PREFIX = 'pinecurl.rebalancing.completion-proof';

function getStorageKey(surface: RebalancingSurface, userId: string) {
  return `${STORAGE_PREFIX}.${surface}.${userId}`;
}

function canUseStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

export function readPersistedRebalancingCompletionProof(
  surface: RebalancingSurface,
  userId?: string,
): PersistedRebalancingCompletionProof | null {
  if (!userId || !canUseStorage()) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getStorageKey(surface, userId));
    if (!raw) return null;
    return JSON.parse(raw) as PersistedRebalancingCompletionProof;
  } catch {
    return null;
  }
}

export function writePersistedRebalancingCompletionProof(
  surface: RebalancingSurface,
  userId: string,
  proof: PersistedRebalancingCompletionProof,
) {
  if (!canUseStorage()) {
    return;
  }

  try {
    window.localStorage.setItem(getStorageKey(surface, userId), JSON.stringify(proof));
  } catch {
    // Ignore localStorage failures and continue with in-memory truth.
  }
}

export function clearPersistedRebalancingCompletionProof(
  surface: RebalancingSurface,
  userId?: string,
) {
  if (!userId || !canUseStorage()) {
    return;
  }

  try {
    window.localStorage.removeItem(getStorageKey(surface, userId));
  } catch {
    // Ignore localStorage failures and continue with in-memory truth.
  }
}

export function formatAssetCount(count: number): string {
  return `${count} asset${count === 1 ? '' : 's'}`;
}

export function determineRebalancingDominantState(params: {
  hasTargets: boolean;
  remainingOutOfToleranceCount: number;
  hasSuggestedTrade: boolean;
}): RebalancingDominantState {
  if (!params.hasTargets) {
    return 'save-targets';
  }

  if (params.remainingOutOfToleranceCount === 0) {
    return 'within-tolerance';
  }

  if (params.hasSuggestedTrade) {
    return 'review-suggested-trades';
  }

  return 'review-largest-drifts';
}

export function getRebalancingDominantTone(
  state: RebalancingDominantState,
): 'warning' | 'info' | 'success' {
  if (state === 'save-targets') return 'warning';
  if (state === 'within-tolerance') return 'success';
  return 'info';
}

export function buildCompletionProofLabel(params: {
  state: RebalancingDominantState;
  afterAssetsWithDrift: number;
  topPrioritySymbol?: string;
  topSuggestedTradeSymbol?: string;
}): string {
  if (params.state === 'save-targets') {
    return 'Save targets to turn drift into durable completion proof';
  }

  if (params.state === 'within-tolerance') {
    return 'Portfolio is within tolerance against the saved target mix';
  }

  if (params.state === 'review-suggested-trades') {
    return params.topSuggestedTradeSymbol
      ? `Top advisory trade: ${params.topSuggestedTradeSymbol}`
      : 'Review the top advisory trade next';
  }

  if (params.topPrioritySymbol) {
    return `${params.topPrioritySymbol} remains the highest-priority drift`;
  }

  return `${formatAssetCount(params.afterAssetsWithDrift)} still outside tolerance`;
}

function buildSavedTargetsProofLabel(params: {
  targetCoverageCount: number;
  trackedAssetCount: number;
  beforeAssetsWithDrift?: number;
  afterAssetsWithDrift: number;
}): string {
  const coverage = `${params.targetCoverageCount}/${params.trackedAssetCount}`;
  const driftDelta =
    params.beforeAssetsWithDrift !== undefined
      ? `${formatAssetCount(params.beforeAssetsWithDrift)} → ${formatAssetCount(params.afterAssetsWithDrift)}`
      : formatAssetCount(params.afterAssetsWithDrift);

  return `Saved targets updated (${coverage} assets, ${driftDelta})`;
}

function getSharedProofContext(params: {
  existingProof?: PersistedRebalancingCompletionProof | null;
  handoff?: DriftHandoffContext | null;
  beforeAssetsWithDrift?: number;
  targetUpdatedAt?: string | null;
}) {
  const matchingExisting =
    params.existingProof &&
    params.existingProof.targetUpdatedAt &&
    params.targetUpdatedAt &&
    params.existingProof.targetUpdatedAt === params.targetUpdatedAt
      ? params.existingProof
      : null;
  const preservedGoalsContext =
    params.handoff ??
    (params.existingProof?.source === 'goals'
      ? {
          source: 'goals' as const,
          proofLabel: params.existingProof.proofLabel,
          proofOccurredAt: params.existingProof.proofOccurredAt ?? null,
          activeGoalName: params.existingProof.activeGoalName ?? null,
          tolerancePct: params.existingProof.goalTolerancePct ?? null,
          driftedAssetCount:
            params.existingProof.beforeAssetsWithDrift ?? params.existingProof.afterAssetsWithDrift,
          topDriftSymbol: params.existingProof.topDriftSymbol ?? null,
          topDriftPct: params.existingProof.topDriftPct ?? null,
          dominantAction:
            params.existingProof.dominantState === 'save-targets'
              ? ('save-targets' as const)
              : params.existingProof.dominantState === 'review-suggested-trades'
                ? ('review-suggested-trades' as const)
                : ('review-largest-drifts' as const),
          destination: 'rebalancing-targets' as const,
        }
      : null);

  return {
    beforeAssetsWithDrift:
      params.beforeAssetsWithDrift ??
      matchingExisting?.beforeAssetsWithDrift ??
      preservedGoalsContext?.driftedAssetCount,
    source:
      preservedGoalsContext?.source ??
      matchingExisting?.source ??
      (params.beforeAssetsWithDrift !== undefined ? ('local-save' as const) : undefined),
    proofLabel: preservedGoalsContext?.proofLabel,
    proofOccurredAt:
      preservedGoalsContext?.proofOccurredAt ??
      matchingExisting?.proofOccurredAt ??
      params.targetUpdatedAt ??
      undefined,
    activeGoalName: preservedGoalsContext?.activeGoalName ?? undefined,
    goalTolerancePct:
      preservedGoalsContext?.tolerancePct ?? matchingExisting?.goalTolerancePct ?? undefined,
    topDriftSymbol: preservedGoalsContext?.topDriftSymbol ?? undefined,
    topDriftPct: preservedGoalsContext?.topDriftPct ?? undefined,
  };
}

export function buildTargetsCompletionProofSnapshot(
  response: RebalancingResponse,
  options: {
    existingProof?: PersistedRebalancingCompletionProof | null;
    handoff?: DriftHandoffContext | null;
    beforeAssetsWithDrift?: number;
  } = {},
): PersistedRebalancingCompletionProof {
  const targetCoverageCount = response.allocations.filter((allocation) => allocation.targetPct > 0).length;
  const trackedAssetCount = response.allocations.length;
  const remainingOutOfToleranceCount =
    response.proof?.remainingOutOfToleranceCount ?? response.assetsWithDrift;
  const topPriorityAsset =
    response.proof?.topPriorityAsset ??
    [...response.allocations].sort((left, right) => Math.abs(right.driftPct) - Math.abs(left.driftPct))[0] ??
    null;
  const topSuggestedTrade = response.proof?.topSuggestedTrade ?? response.suggestedTrades[0] ?? null;
  const targetUpdatedAt = response.proof?.targetUpdatedAt ?? null;
  const shared = getSharedProofContext({
    existingProof: options.existingProof,
    handoff: options.handoff,
    beforeAssetsWithDrift: options.beforeAssetsWithDrift,
    targetUpdatedAt,
  });
  const dominantState =
    response.proof?.dominantState ??
    determineRebalancingDominantState({
      hasTargets: targetCoverageCount > 0,
      remainingOutOfToleranceCount,
      hasSuggestedTrade: Boolean(topSuggestedTrade),
    });

  return {
    source: shared.source,
    proofLabel:
      shared.source === 'local-save'
        ? buildSavedTargetsProofLabel({
            targetCoverageCount,
            trackedAssetCount,
            beforeAssetsWithDrift: shared.beforeAssetsWithDrift,
            afterAssetsWithDrift: remainingOutOfToleranceCount,
          })
        : shared.proofLabel ??
          buildCompletionProofLabel({
            state: dominantState,
            afterAssetsWithDrift: remainingOutOfToleranceCount,
            topPrioritySymbol: topPriorityAsset?.symbol ?? undefined,
            topSuggestedTradeSymbol: topSuggestedTrade?.symbol ?? undefined,
          }),
    proofOccurredAt: shared.proofOccurredAt,
    activeGoalName: shared.activeGoalName,
    goalTolerancePct: shared.goalTolerancePct,
    dominantState,
    beforeAssetsWithDrift: shared.beforeAssetsWithDrift,
    afterAssetsWithDrift: remainingOutOfToleranceCount,
    topDriftSymbol: shared.topDriftSymbol ?? topPriorityAsset?.symbol ?? undefined,
    topDriftPct: shared.topDriftPct ?? topPriorityAsset?.driftPct ?? undefined,
    targetCoverageCount,
    trackedAssetCount,
    topPrioritySymbol: topPriorityAsset?.symbol ?? undefined,
    topPriorityDriftPct: topPriorityAsset?.driftPct ?? undefined,
    topSuggestedTradeSymbol: topSuggestedTrade?.symbol ?? undefined,
    topSuggestedTradeAction: topSuggestedTrade?.action ?? undefined,
    topSuggestedTradeAmountUsd: topSuggestedTrade?.amountUsd ?? undefined,
    targetUpdatedAt,
  };
}

export function buildAdvisorCompletionProofSnapshot(
  advisor: RebalanceAdvisorData,
  options: {
    existingProof?: PersistedRebalancingCompletionProof | null;
    handoff?: DriftHandoffContext | null;
    beforeAssetsWithDrift?: number;
  } = {},
): PersistedRebalancingCompletionProof {
  const remainingOutOfToleranceCount =
    advisor.proof?.remainingOutOfToleranceCount ??
    advisor.drifts.filter((drift) => drift.driftStatus !== 'within').length;
  const topPriorityAsset =
    advisor.proof?.topPriorityAsset ??
    [...advisor.drifts]
      .filter((drift) => drift.driftStatus !== 'within')
      .sort((left, right) => Math.abs(right.driftPct) - Math.abs(left.driftPct))[0] ??
    null;
  const topSuggestedTrade =
    advisor.proof?.topSuggestedTrade ??
    [...advisor.drifts]
      .filter((drift) => drift.driftStatus !== 'within' && Math.abs(drift.suggestedTradeUsd) > 1)
      .sort((left, right) => Math.abs(right.suggestedTradeUsd) - Math.abs(left.suggestedTradeUsd))
      .map((drift) => ({
        action: drift.suggestedTradeUsd > 0 ? ('BUY' as const) : ('SELL' as const),
        symbol: drift.symbol,
        amountUsd: Math.abs(drift.suggestedTradeUsd),
      }))[0] ??
    null;
  const targetUpdatedAt = advisor.proof?.targetUpdatedAt ?? null;
  const shared = getSharedProofContext({
    existingProof: options.existingProof,
    handoff: options.handoff,
    beforeAssetsWithDrift: options.beforeAssetsWithDrift,
    targetUpdatedAt,
  });
  const dominantState =
    advisor.proof?.dominantState ??
    determineRebalancingDominantState({
      hasTargets: advisor.targets.length > 0,
      remainingOutOfToleranceCount,
      hasSuggestedTrade: Boolean(topSuggestedTrade),
    });

  return {
    source: shared.source,
    proofLabel:
      shared.proofLabel ??
      buildCompletionProofLabel({
        state: dominantState,
        afterAssetsWithDrift: remainingOutOfToleranceCount,
        topPrioritySymbol: topPriorityAsset?.symbol ?? undefined,
        topSuggestedTradeSymbol: topSuggestedTrade?.symbol ?? undefined,
      }),
    proofOccurredAt: shared.proofOccurredAt,
    activeGoalName: shared.activeGoalName,
    goalTolerancePct: shared.goalTolerancePct,
    dominantState,
    beforeAssetsWithDrift: shared.beforeAssetsWithDrift,
    afterAssetsWithDrift: remainingOutOfToleranceCount,
    topDriftSymbol: shared.topDriftSymbol ?? topPriorityAsset?.symbol ?? undefined,
    topDriftPct: shared.topDriftPct ?? topPriorityAsset?.driftPct ?? undefined,
    targetCoverageCount: advisor.targets.length,
    trackedAssetCount: advisor.drifts.length,
    topPrioritySymbol: topPriorityAsset?.symbol ?? undefined,
    topPriorityDriftPct: topPriorityAsset?.driftPct ?? undefined,
    topSuggestedTradeSymbol: topSuggestedTrade?.symbol ?? undefined,
    topSuggestedTradeAction: topSuggestedTrade?.action ?? undefined,
    topSuggestedTradeAmountUsd: topSuggestedTrade?.amountUsd ?? undefined,
    targetUpdatedAt,
  };
}
